import { useEffect, useState } from 'react';
import axios from 'axios';
import { MousePointerClick, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Card de analytics de cliques no link de convite.
 * Funciona para afiliado e parceiro:
 *  - endpoint: URL completa ou path (ex: "/affiliates/me/clicks" ou "/partners/panel/{token}/clicks")
 *  - authHeader: headers opcionais (token de afiliado) — parceiro usa token no path, sem Authorization.
 */
export const ClickAnalyticsCard = ({ endpoint, authHeader, title = 'Cliques no seu link (30 dias)' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const url = endpoint.startsWith('http') ? endpoint : `${API}${endpoint}`;
        const res = await axios.get(url, { headers: authHeader || {} });
        if (mounted) setData(res.data);
      } catch {
        if (mounted) setData({ total_clicks_30d: 0, direct_clicks: 0, by_source: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, [endpoint, authHeader]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="click-analytics-loading">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-200 rounded w-1/3" />
          <div className="h-8 bg-slate-200 rounded w-1/4" />
        </div>
      </div>
    );
  }

  const total = data?.total_clicks_30d || 0;
  const sources = data?.by_source || [];
  const direct = data?.direct_clicks || 0;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="click-analytics-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MousePointerClick className="w-5 h-5 text-[#C5A059]" />
          <h3 className="text-sm font-bold text-[#0A2647]">{title}</h3>
        </div>
        <div className="text-right">
          <p className="text-2xl font-extrabold text-[#0A2647]" data-testid="click-total-count">{total}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">cliques</p>
        </div>
      </div>

      {total === 0 ? (
        <div className="py-6 text-center">
          <p className="text-sm text-slate-500 italic mb-1">Nenhum clique nos ultimos 30 dias.</p>
          <p className="text-xs text-slate-400">Compartilhe seu link para comecar a ver os resultados aqui.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {sources.map((s) => {
            const pct = Math.round((s.count / total) * 100);
            return (
              <div key={s.slug} data-testid={`click-source-${s.slug}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-slate-700 truncate max-w-[70%]">{s.name}</span>
                  <span className="text-xs text-[#C5A059] font-bold shrink-0">{s.count} {s.count === 1 ? 'clique' : 'cliques'}</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#C5A059] to-[#d4af61] rounded-full transition-all duration-500"
                    style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          {direct > 0 && (
            <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-3">
              <span className="text-xs text-slate-600 italic flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" />Direto (sem origem)</span>
              <span className="text-xs text-slate-600 font-semibold">{direct}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
